import React, { useState, useRef } from 'react';
import { Card } from './ui/card';
import { Button } from './ui/button';
import { Upload, X, FileText, GripVertical, Camera } from 'lucide-react';
import { toast } from 'sonner';

export const PhotoUploadEnhanced = ({ 
  photos = [], 
  onChange, 
  maxPhotos = 5, 
  acceptPDF = false, 
  fieldId = 'photo-upload',
  enableReorder = true,
  enableCamera = true
}) => {
  const [dragOver, setDragOver] = useState(false);
  const [draggingIndex, setDraggingIndex] = useState(null);
  const [uploadProgress, setUploadProgress] = useState({});
  const fileInputRef = useRef(null);
  
  const photoArray = Array.isArray(photos) ? photos : [];

  const handleDragEnter = (e) => {
    e.preventDefault();
    e.stopPropagation();
    setDragOver(true);
  };

  const handleDragLeave = (e) => {
    e.preventDefault();
    e.stopPropagation();
    setDragOver(false);
  };

  const handleDragOver = (e) => {
    e.preventDefault();
    e.stopPropagation();
  };

  const handleDrop = (e) => {
    e.preventDefault();
    e.stopPropagation();
    setDragOver(false);
    
    const files = Array.from(e.dataTransfer.files);
    handleFiles(files);
  };

  const handleFileSelect = (e) => {
    const files = Array.from(e.target.files);
    handleFiles(files);
    if (fileInputRef.current) {
      fileInputRef.current.value = '';
    }
  };

  const handleFiles = (files) => {
    if (photoArray.length + files.length > maxPhotos) {
      toast.error(`Maksimum ${maxPhotos} dosya yükleyebilirsiniz`);
      return;
    }

    const validFiles = [];
    const invalidFiles = [];
    
    files.forEach(file => {
      const isImage = file.type.startsWith('image/');
      const isPDF = acceptPDF && file.type === 'application/pdf';
      
      if (!isImage && !isPDF) {
        const allowedTypes = acceptPDF ? 'PNG, JPG, JPEG, PDF' : 'PNG, JPG, JPEG';
        invalidFiles.push(`${file.name} (sadece ${allowedTypes} dosyaları)`);
        return;
      }
      
      if (file.size > 5 * 1024 * 1024) {
        invalidFiles.push(`${file.name} (maksimum 5MB)`);
        return;
      }
      
      validFiles.push(file);
    });

    if (invalidFiles.length > 0) {
      toast.error(`Geçersiz dosyalar: ${invalidFiles.join(', ')}`);
    }

    if (validFiles.length > 0) {
      const progressUpdates = {};
      validFiles.forEach((file, index) => {
        const fileId = `${Date.now()}-${index}`;
        progressUpdates[fileId] = 0;
      });
      setUploadProgress(progressUpdates);

      const fileReaders = validFiles.map((file, index) => {
        const fileId = `${Date.now()}-${index}`;
        
        return new Promise((resolve) => {
          const reader = new FileReader();
          
          let progress = 0;
          const progressInterval = setInterval(() => {
            progress += 20;
            if (progress <= 100) {
              setUploadProgress(prev => ({
                ...prev,
                [fileId]: progress
              }));
            }
            if (progress >= 100) {
              clearInterval(progressInterval);
            }
          }, 100);
          
          reader.onloadend = () => {
            clearInterval(progressInterval);
            setUploadProgress(prev => {
              const newProgress = { ...prev };
              delete newProgress[fileId];
              return newProgress;
            });
            resolve(file);
          };
          
          reader.readAsDataURL(file);
        });
      });

      Promise.all(fileReaders).then(() => {
        const updatedPhotos = [...photoArray, ...validFiles];
        onChange(updatedPhotos);
        toast.success(`${validFiles.length} dosya eklendi`);
      });
    }
  };

  const handleRemove = (index) => {
    const updatedPhotos = photoArray.filter((_, i) => i !== index);
    onChange(updatedPhotos);
    toast.success('Dosya silindi');
  };

  const handleDragStart = (e, index) => {
    if (!enableReorder) return;
    e.dataTransfer.effectAllowed = 'move';
    setDraggingIndex(index);
  };

  const handleDragEnd = () => {
    setDraggingIndex(null);
  };

  const handleDragOverItem = (e, index) => {
    if (!enableReorder) return;
    e.preventDefault();
    
    if (draggingIndex === null || draggingIndex === index) return;
    
    const items = [...photoArray];
    const draggedItem = items[draggingIndex];
    
    items.splice(draggingIndex, 1);
    items.splice(index, 0, draggedItem);
    
    onChange(items);
    setDraggingIndex(index);
  };

  const hasUploadingFiles = Object.keys(uploadProgress).length > 0;

  return (
    <div className="space-y-4" data-testid="photo-upload-enhanced">
      {photoArray.length < maxPhotos && (
        <div
          onDragEnter={handleDragEnter}
          onDragOver={handleDragOver}
          onDragLeave={handleDragLeave}
          onDrop={handleDrop}
          className={`transition-all ${dragOver ? 'scale-[1.02]' : ''}`}
        >
          <input
            type="file"
            id={fieldId}
            ref={fileInputRef}
            accept={acceptPDF ? "image/*,.pdf" : "image/*"}
            multiple
            onChange={handleFileSelect}
            className="hidden"
            data-testid={fieldId}
            {...(enableCamera && { capture: "environment" })}
          />
          <label htmlFor={fieldId}>
            <Card className={`border-2 border-dashed transition-all cursor-pointer p-8 text-center ${
              dragOver 
                ? 'border-orange-500 bg-orange-100 scale-[1.02]' 
                : 'border-gray-300 hover:border-orange-400 bg-gray-50 hover:bg-orange-50'
            }`}>
              <div className="flex flex-col items-center gap-3">
                <div className={`p-4 rounded-full transition-colors ${
                  dragOver ? 'bg-orange-200' : 'bg-orange-100'
                }`}>
                  {enableCamera ? (
                    <Camera className="h-8 w-8 text-orange-600" />
                  ) : (
                    <Upload className="h-8 w-8 text-orange-600" />
                  )}
                </div>
                <div>
                  <p className="font-semibold text-gray-700">
                    {dragOver ? 'Drop files here' : (acceptPDF ? 'Dosya Yükle' : 'Fotoğraf Yükle')}
                  </p>
                  <p className="text-sm text-gray-500 mt-1">
                    {photoArray.length} / {maxPhotos} {acceptPDF ? 'dosya' : 'fotoğraf'} yüklendi
                  </p>
                  <p className="text-xs text-gray-400 mt-2">
                    {acceptPDF ? 'PNG, JPG, JPEG, PDF' : 'PNG, JPG, JPEG'} - Maksimum 5MB - En fazla {maxPhotos} {acceptPDF ? 'dosya' : 'fotoğraf'}
                  </p>
                  <p className="text-xs text-blue-600 mt-1 font-medium">
                    Drag & drop files or click to browse
                  </p>
                </div>
              </div>
            </Card>
          </label>
        </div>
      )}

      {hasUploadingFiles && (
        <div className="space-y-2">
          {Object.entries(uploadProgress).map(([fileId, progress]) => (
            <div key={fileId} className="bg-gray-100 rounded-lg p-3">
              <div className="flex items-center justify-between mb-2">
                <span className="text-sm text-gray-700">Uploading...</span>
                <span className="text-sm font-medium text-orange-600">{progress}%</span>
              </div>
              <div className="w-full bg-gray-200 rounded-full h-2">
                <div 
                  className="bg-orange-500 h-2 rounded-full transition-all duration-300"
                  style={{ width: `${progress}%` }}
                />
              </div>
            </div>
          ))}
        </div>
      )}

      {photoArray.length > 0 && (
        <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-5 gap-4">
          {photoArray.map((photo, index) => {
            let isPDF = false;
            let previewUrl;
            let fileName;
            
            if (typeof photo === 'string') {
              previewUrl = photo;
              isPDF = photo.startsWith('data:application/pdf');
            } else if (photo instanceof File) {
              previewUrl = URL.createObjectURL(photo);
              isPDF = photo.type === 'application/pdf';
              fileName = photo.name;
            } else {
              console.warn('Unknown photo type:', photo);
              return null;
            }
            
            return (
              <Card
                key={index}
                draggable={enableReorder}
                onDragStart={(e) => handleDragStart(e, index)}
                onDragEnd={handleDragEnd}
                onDragOver={(e) => handleDragOverItem(e, index)}
                className={`relative group overflow-hidden aspect-square transition-all ${
                  enableReorder ? 'cursor-move' : ''
                } ${draggingIndex === index ? 'opacity-50 scale-95' : ''}`}
                data-testid={`photo-preview-${index}`}
              >
                {isPDF ? (
                  <div className="w-full h-full flex flex-col items-center justify-center bg-red-50">
                    <FileText className="h-12 w-12 text-red-600 mb-2" />
                    <p className="text-xs text-gray-700 px-2 text-center break-all">{fileName || 'PDF Document'}</p>
                  </div>
                ) : (
                  <img
                    src={previewUrl}
                    alt={`Preview ${index + 1}`}
                    className="w-full h-full object-cover"
                  />
                )}
                
                {enableReorder && (
                  <div className="absolute top-2 right-2 bg-black bg-opacity-70 p-1 rounded opacity-0 group-hover:opacity-100 transition-opacity">
                    <GripVertical className="h-4 w-4 text-white" />
                  </div>
                )}
                
                <div className="absolute inset-0 bg-black bg-opacity-0 group-hover:bg-opacity-50 transition-all duration-200 flex items-center justify-center">
                  <Button
                    type="button"
                    variant="destructive"
                    size="sm"
                    onClick={() => handleRemove(index)}
                    className="opacity-0 group-hover:opacity-100 transition-opacity"
                    data-testid={`delete-photo-${index}`}
                  >
                    <X className="h-4 w-4 mr-1" />
                    Sil
                  </Button>
                </div>
                
                <div className="absolute top-2 left-2 bg-black bg-opacity-70 text-white text-xs px-2 py-1 rounded font-medium">
                  {index + 1}
                </div>
              </Card>
            );
          })}
        </div>
      )}

      {photoArray.length === 0 && (
        <div className="text-center py-4 text-gray-500 text-sm">
          Henüz {acceptPDF ? 'dosya' : 'fotoğraf'} yüklenmedi
        </div>
      )}
    </div>
  );
};

export default PhotoUploadEnhanced;
